import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Api } from 'bimplus-websdk';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import {
  ExtraQueryParams,
  ImportAttachmentData,
  UploadMode,
  UploadProgressCallback,
} from './import-adapter.interface';

/**
 * ImportAdapterService - Adapter over bimplus-websdk Import API.
 */
@Injectable({
  providedIn: 'root',
})
export class ImportAdapterService {
  private readonly api: Api = inject(BIMPLUS_API);

  /**
   * Imports model data asynchronously.
   *
   * Supports both direct file upload and attachment-based import.
   * When replaceModelData mode is enabled, existing model data will be replaced.
   *
   * @param projectId - The project ID.
   * @param modelId - The model (division) ID.
   * @param data - The data to import (FormData or ImportAttachmentData).
   * @param progressCallback - Optional callback for upload progress tracking.
   * @param uploadMode - Upload mode: 'replaceModelData' to replace existing data, or empty string.
   * @param extraQueryParams - Optional additional query parameters.
   * @returns Observable that resolves when import is initiated.
   */
  postModel(
    projectId: string,
    modelId: string,
    data: FormData | ImportAttachmentData,
    progressCallback?: UploadProgressCallback,
    uploadMode?: UploadMode,
    extraQueryParams?: ExtraQueryParams
  ): Observable<unknown> {
    const api = this.api;

    const queryParams: ExtraQueryParams = { ...extraQueryParams };

    if (uploadMode === 'replaceModelData') {
      queryParams['replaceModelData'] = true;
    }

    const setup: Record<string, unknown> = {
      preparedData: data,
      processData: false,
      dataType: null,
      xhrUploadProgress: progressCallback,
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    };

    // Handle attachment-based import
    if (this.isAttachmentData(data)) {
      if (data.version) {
        setup['queryParams'] = setup['queryParams'] || {};
        (setup['queryParams'] as ExtraQueryParams)['version'] = data.version;
      }

      return this.api.rxRequest(
        api.request.post(
          api.getApiTeamUrl() +
            'projects/' +
            projectId +
            '/model/' +
            modelId +
            '/attachment/' +
            data.attachment_id +
            '/importasync',
          data,
          setup
        )
      );
    }

    // Handle direct file upload
    setup['contentType'] = false;
    return this.api.rxRequest(
      api.request.post(
        api.getApiTeamUrl() + 'projects/' + projectId + '/model/' + modelId + '/importasync',
        data,
        setup
      )
    );
  }

  /**
   * Type guard to check if data is ImportAttachmentData.
   */
  private isAttachmentData(data: FormData | ImportAttachmentData): data is ImportAttachmentData {
    return data && typeof data === 'object' && 'attachment_id' in data;
  }
}
